import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { isClient } from './environment';

const getUserTrackingInfo = () => {
  if (!isClient) return { id: undefined, type: undefined };

  const { personId, agentId } = window.__INITIAL_STATE__ || {};
  const id = personId || agentId;
  const type = agentId ? 'agent' : 'consumer';

  return {
    id,
    type,
  };
};

export const filterNoisyBreadCrumbs = (breadcrumb, hint) => {
  // Filter console logs as we use them for debugging *a lot* and they are not *that* important
  if (breadcrumb.category === 'console') {
    return null;
  }

  // Filter xhr calls to third party websites used for product analysis
  // https://cdn.optimizely.com
  // https://s.yimg.com
  // https://api.amplitude.com

  if (
    breadcrumb.category === 'xhr' &&
    (breadcrumb.data.url.indexOf('https://cdn.optimizely.com') !== -1 ||
      breadcrumb.data.url.indexOf('https://api.amplitude.com') !== -1 ||
      breadcrumb.data.url.indexOf('https://s.yimg.com') !== -1)
  ) {
    return null;
  }

  if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
    const data = {
      // Needed for accessing sentry request body as it is inside dangling object
      // eslint-disable-next-line
      requestBody: hint.xhr.__sentry_xhr__.body,
      response: hint.xhr.response,
      responseUrl: hint.xhr.responseURL,
    };

    return { ...breadcrumb, data };
  }

  return breadcrumb;
};

const CODES_TO_IGNORE = ['DOWNSTREAM_SERVICE_ERROR', 'INTERNAL_SERVER_ERROR'];

const config = {
  integrations: [new Integrations.BrowserTracing()],
  dsn: isClient && window.__CONFIG__.sentryDSN,
  release: isClient && window.__CONFIG__.version,
  environment: isClient && window.__CONFIG__.environment,
  tracesSampleRate: isClient && window.__CONFIG__.tracesSampleRate,
  // Ignore errors can have strings as well as regex string in pattern /<regex>/
  ignoreErrors: [
    // Begin -- community based common ignore rules for common things, like Facebook, Chrome extensions, etc.//
    // More info here https://docs.sentry.io/platforms/node/guides/gcp-functions/configuration/filtering/#decluttering-sentry

    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'atomicFindClose',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Generic error code from errors outside the security sandbox
    // You can delete this if using raven.js > 1.0, which ignores these automatically.
    'Script error.',
    // End -- community based common ignore rules for common things, like Facebook, Chrome extensions, etc.//

    // Begin -- Application specific errors
    /400: Bad Request/,
    /Load failed/,
    /Failed to fetch/,
    /Network Error/,
    /NetworkError/,
    /A cross-origin error was thrown/,
    /Request aborted/,
    /The request timed out/,
    /Session timeout, please retry/,
    /timeout of 0ms exceeded/,
    /Non-Error promise rejection captured with keys/,
    /Policy Identifier Not Found/,

    /$window is not defined/,
    /User must be an agent/,

    /AbortError: The user aborted a request/,
    /403: Forbidden/,
    /Request failed with status code 401/,
    /No userid found--user must be logged in/,
    /Request is not authorized./,
    'INTERNAL_SERVER_ERROR',
    'FORBIDDEN',
    /GraphqlError:undefined/,
    // End -- Application specific errors

    // Osano specific errors
    /Can't find variable: ttq/,
    /ttq is not defined/,
    /Can't find variable: fbq/,
    /fbq is not defined/,
  ],
  denyUrls: [
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
  ],

  beforeBreadcrumb(breadcrumb, hint) {
    return filterNoisyBreadCrumbs(breadcrumb, hint);
  },
  beforeSend(event) {
    const { code } = event.tags || {};
    if (code && CODES_TO_IGNORE.includes(code)) return null;

    return event;
  },
};

Sentry.init(config);

Sentry.setUser(getUserTrackingInfo());

const Logger = Sentry;

export { Logger };
